(() => {
  'use strict';
  document.documentElement.classList.add('flux-js');
  const config = window.FluxEditionTheme || {};
  const rootUrl = config.root || '/';
  const message = (key, fallback = key) => config.messages?.[key] || fallback;
  const wishlistKey = 'flux-edition-wishlist';
  let wishlistMap = {};
  let cartBusy = false;
  const money = (cents) => new Intl.NumberFormat(config.locale || 'en-AU', {style: 'currency', currency: config.currency || 'AUD', currencyDisplay: 'code',minimumFractionDigits:0,maximumFractionDigits:2}).format(Number(cents) / 100);
  const previewUrl = (value) => {
    const url = new URL(value, window.location.origin);
    return url.pathname + url.search + url.hash;
  };
  const nativeUrl = (path) => previewUrl(rootUrl + path.replace(/^\//, ''));
  if (window.location.pathname === '/shop' || window.location.pathname === '/shop/') {
    const destination = new URL(nativeUrl('collections/all'), window.location.origin);
    for (const [name, value] of new URLSearchParams(window.location.search)) destination.searchParams.set(name, value);
    window.location.replace(destination.href);
    return;
  }
  const readWishlist = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      return Array.isArray(saved) ? [...new Set(saved.filter(value => typeof value === 'string').map(value => wishlistMap[value] || value))] : [];
    } catch { return []; }
  };
  function syncWishlist() {
    const handles = readWishlist();
    document.querySelectorAll('[data-wishlist-count]:not([data-wishlist-page-count])').forEach(node => { node.textContent = '(' + handles.length + ')'; });
    document.querySelectorAll('[data-wishlist-handle]').forEach(button => {
      const saved = handles.includes(button.dataset.wishlistHandle);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', message(saved ? 'removeSaved' : 'save'));
      if (!button.querySelector('svg')) button.textContent = saved ? '♥' : '♡';
    });
  }
  function writeWishlist(handles) {
    try { localStorage.setItem(wishlistKey, JSON.stringify([...new Set(handles)])); } catch { /* Storage is optional, not a cart fallback. */ }
    syncWishlist();
  }
  function syncCart(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = '(' + cart.item_count + ')'; });
  }
  async function nativeJsonRequest(path, options = {}) {
    if (config.previewOnly && String(options.method || 'GET').toUpperCase() !== 'GET') {
      throw new Error(message('previewUnavailable', 'This Australia preview is not open for orders.'));
    }
    const controller = new AbortController();
    let timer;
    // A POST may have reached Shopify before an abort. Never automatically retry it.
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        const error = new Error(message('cartOutcomeUnknown', 'The request timed out. Its result is unknown. Check your Shopify bag before trying again.'));
        error.nativeRequestTimedOut = true;
        error.nativeOutcomeUnknown = true;
        reject(error);
        controller.abort();
      }, 15000);
    });
    const request = (async () => {
      const response = await fetch(previewUrl(rootUrl + path.replace(/^\//, ''), false), {...options, credentials: 'same-origin', signal: controller.signal});
      const data = await response.json();
      if (!response.ok) {
        const error = new Error(typeof data.description === 'string' ? data.description : (data.message || message('error')));
        error.nativeResponseError = true;
        throw error;
      }
      return data;
    })();
    try { return await Promise.race([request, timeout]); }
    catch (error) {
      if (options.method === 'POST' && !error.nativeResponseError) error.nativeOutcomeUnknown = true;
      throw error;
    }
    finally { clearTimeout(timer); }
  }
  async function cartRequest(path, payload) {
    return nativeJsonRequest(path, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(payload)});
  }
  async function readNativeCart() {
    const cart = await nativeJsonRequest('cart.js', {headers: {Accept: 'application/json'}});
    syncCart(cart);
    return cart;
  }
  function bagMessage(node, text, includeBagLink = false) {
    if (!node) return;
    node.textContent = text;
    if (includeBagLink) {
      const link = document.createElement('a');
      link.href = nativeUrl('cart');
      link.textContent = message('checkBag', 'Check your Shopify bag');
      node.append(' ', link);
    }
  }
  function cartError(error) {
    const node = document.querySelector('[data-cart-error]');
    if (node) { bagMessage(node, error.message || message('error'), error.nativeOutcomeUnknown); node.hidden = false; }
  }
  async function changeCart(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const form = document.querySelector('[data-cart-form]');
    form?.querySelectorAll('button,input').forEach(node => { node.disabled = true; });
    try {
      const cart = await cartRequest('cart/change.js', {id: key, quantity});
      syncCart(cart);
      window.location.reload();
    } catch (error) {
      cartError(error);
      form?.querySelectorAll('button,input').forEach(node => { node.disabled = false; });
      cartBusy = false;
    }
  }
  async function renderWishlist() {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    const handles = readWishlist();
    const empty = document.querySelector('[data-wishlist-empty]');
    const count = document.querySelector('[data-wishlist-page-count]');
    if (count) count.textContent = handles.length + (handles.length === 1 ? ' saved piece' : ' saved pieces');
    if (empty) empty.hidden = handles.length > 0;
    grid.replaceChildren();
    const results = await Promise.allSettled(handles.map(async handle => {
      const response = await fetch(nativeUrl('products/' + encodeURIComponent(handle) + '.js'), {credentials: 'same-origin', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Saved product is no longer available.');
      return response.json();
    }));
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') {
        const error = document.createElement('div');
        error.className = 'wishlist-load-error';
        error.textContent = 'A saved piece is unavailable. ';
        const remove = document.createElement('button');
        remove.type = 'button'; remove.dataset.wishlistHandle = handles[index];
        remove.textContent = message('removeSaved');
        error.append(remove); grid.append(error);
        return;
      }
      const product = result.value;
      const article = document.createElement('article'); article.className = 'product-card';
      const imageLink = document.createElement('a'); imageLink.className = 'product-image';
      imageLink.href = nativeUrl('products/' + encodeURIComponent(product.handle));
      imageLink.setAttribute('aria-label', product.title);
      if (product.featured_image) {
        const img = document.createElement('img'); img.src = product.featured_image; img.alt = product.title; img.loading = 'lazy'; img.width = 720; img.height = 900;
        imageLink.append(img);
      }
      const save = document.createElement('button'); save.type = 'button'; save.className = 'wishlist-button is-active'; save.dataset.wishlistHandle = product.handle;
      const copy = document.createElement('a'); copy.className = 'product-copy'; copy.href = imageLink.href;
      const vendor = document.createElement('strong'); vendor.textContent = product.vendor;
      const title = document.createElement('span'); title.textContent = product.title;
      const price = document.createElement('span'); price.className = 'price'; price.textContent = (product.price_varies ? 'From ' : '') + money(product.price);
      copy.append(vendor, title, price); article.append(imageLink, save, copy); grid.append(article);
    });
    syncWishlist();
  }
  function openDialog(dialog, trigger) {
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    dialog.fluxTrigger = trigger || document.activeElement;
    dialog.showModal();
    document.body.classList.add('has-flux-dialog');
    const input = dialog.querySelector('input[type=search]');
    if (input) input.focus();
  }
  document.querySelectorAll('dialog').forEach(dialog => {
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('has-flux-dialog');
      dialog.fluxTrigger?.focus();
    });
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  });
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const open = target.closest('[data-dialog-open]');
    if (open) { openDialog(document.getElementById(open.dataset.dialogOpen), open); return; }
    const close = target.closest('[data-dialog-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    const density = target.closest('[data-catalog-density]');
    if (density) {
      const catalog = density.closest('[data-native-catalog]');
      const grid = catalog?.querySelector('[data-catalog-grid]');
      const columns = Number(density.dataset.catalogDensity);
      if (grid && (columns === 3 || columns === 4)) { grid.classList.toggle('columns-3', columns === 3); grid.classList.toggle('columns-4', columns === 4); catalog.querySelectorAll('[data-catalog-density]').forEach(button => button.setAttribute('aria-pressed', String(button === density))); }
      return;
    }
    const save = target.closest('[data-wishlist-handle]');
    if (save) {
      const handles = readWishlist(), handle = save.dataset.wishlistHandle;
      writeWishlist(handles.includes(handle) ? handles.filter(value => value !== handle) : [...handles, handle]);
      renderWishlist(); return;
    }
    const remove = target.closest('[data-cart-remove]');
    if (remove) { event.preventDefault(); changeCart(remove.dataset.lineKey, 0); }
  });
  document.addEventListener('change', event => {
    const target = event.target;
    if (target instanceof HTMLSelectElement && target.matches('[data-sort-select]')) {
      const url = new URL(window.location.href); url.searchParams.set('sort_by', target.value); url.searchParams.delete('page'); window.location.assign(url.toString());
    }
    if (target instanceof HTMLInputElement && target.matches('[data-cart-quantity]')) {
      const quantity = Number(target.value);
      if (Number.isSafeInteger(quantity) && quantity >= 0) changeCart(target.dataset.lineKey, quantity);
    }
  });
  document.querySelector('[data-cart-form]')?.addEventListener('submit', async event => {
    if (event.submitter?.name === 'checkout') return; // Shopify handles the native checkout POST.
    event.preventDefault();
    if (cartBusy) return;
    const form = event.currentTarget;
    const updates = {};
    for (const input of form.querySelectorAll('[data-cart-quantity]')) {
      const quantity = Number(input.value);
      if (!Number.isSafeInteger(quantity) || quantity < 0) { input.reportValidity(); return; }
      updates[input.dataset.lineKey] = quantity;
    }
    cartBusy = true;
    try { syncCart(await cartRequest('cart/update.js', {updates})); window.location.reload(); }
    catch (error) { cartBusy = false; cartError(error); }
  });
  document.querySelectorAll('[data-product-root]').forEach(productRoot => {
    const json = document.getElementById(productRoot.dataset.productJson);
    if (!json) return;
    let product;
    try { product = JSON.parse(json.textContent); } catch { return; }
    const form = productRoot.querySelector('[data-product-form]');
    const select = form?.querySelector('[data-variant-select]') || form?.querySelector('input[name=id]');
    const gallery = productRoot.querySelector('[data-gallery]');
    const slides = gallery ? [...gallery.querySelectorAll('.product-gallery-slide')] : [];
    const track = gallery?.querySelector('[data-gallery-track]');
    const count = gallery?.querySelector('[data-gallery-count]');
    const dialog = productRoot.querySelector('[data-product-lightbox]');
    const stage = dialog?.querySelector('.product-lightbox-stage');
    const lightboxImage = dialog?.querySelector('[data-lightbox-image]');
    let galleryIndex = 0, zoom = 1, panX = 0, panY = 0, drag = null, swipe = null, suppressClick = false, addBusy = false;
    function setGallery(index) {
      if (!slides.length) return;
      galleryIndex = (index + slides.length) % slides.length;
      if (track) track.style.transform = 'translateX(-' + galleryIndex * 100 + '%)';
      if (count) count.textContent = (galleryIndex + 1) + ' / ' + slides.length;
      gallery?.querySelectorAll('button[data-gallery-index]').forEach(button => {
        const active = Number(button.dataset.galleryIndex) === galleryIndex;
        button.classList.toggle('is-active', active); button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-current', String(active));
      });
      if (dialog?.open) setLightboxImage();
    }
    function applyZoom() {
      if (!stage || !lightboxImage) return;
      const maxX = Math.max(0, (lightboxImage.offsetWidth * zoom - stage.clientWidth) / 2);
      const maxY = Math.max(0, (lightboxImage.offsetHeight * zoom - stage.clientHeight) / 2);
      panX = Math.max(-maxX, Math.min(maxX, panX)); panY = Math.max(-maxY, Math.min(maxY, panY));
      lightboxImage.style.transform = 'translate(' + panX + 'px,' + panY + 'px) scale(' + zoom + ')';
      stage.classList.toggle('is-zoomed', zoom > 1);
      const output = dialog.querySelector('[data-lightbox-zoom-value]');
      if (output) output.textContent = (zoom * 100) + '%';
      dialog.querySelectorAll('[data-lightbox-zoom]').forEach(button => { button.disabled = Number(button.dataset.lightboxZoom) < 0 ? zoom <= 1 : zoom >= 4; });
    }
    function resetZoom() { zoom = 1; panX = 0; panY = 0; drag = null; applyZoom(); }
    function setLightboxImage() {
      const image = slides[galleryIndex]?.querySelector('img');
      if (!image || !lightboxImage) return;
      lightboxImage.src = slides[galleryIndex].dataset.imageUrl || image.currentSrc || image.src;
      lightboxImage.alt = image.alt;
      const output = dialog.querySelector('[data-lightbox-count]');
      if (output) output.textContent = (galleryIndex + 1) + ' / ' + slides.length;
      resetZoom();
    }
    lightboxImage?.addEventListener('load', applyZoom);
    function updateVariant() {
      const variant = product.variants.find(value => String(value.id) === select?.value);
      if (!variant) return;
      const price = productRoot.querySelector('[data-current-price]');
      const compare = productRoot.querySelector('[data-compare-price]');
      const add = form?.querySelector('[data-add-button]');
      if (price) price.textContent = money(variant.price);
      if (compare) { compare.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : ''; compare.hidden = !(variant.compare_at_price > variant.price); }
      if (add) { add.disabled = addBusy || !variant.available; add.textContent = message(variant.available ? 'add' : 'soldOut'); }
      productRoot.querySelectorAll('[data-variant-button]').forEach(button => { const selected = button.dataset.variantId === String(variant.id); button.classList.toggle('selected', selected); button.setAttribute('aria-pressed', String(selected)); });
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      const imageId = variant.featured_media?.id || variant.featured_image?.id;
      if (imageId) { const index = slides.findIndex(slide => slide.dataset.mediaId === String(imageId)); if (index >= 0) setGallery(index); }
    }
    select?.addEventListener('change', updateVariant);
    productRoot.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const size = target.closest('[data-variant-button]');
      if (size && select && !size.disabled) { select.value = size.dataset.variantId; updateVariant(); }
      const step = target.closest('[data-gallery-step]');
      if (step) setGallery(galleryIndex + Number(step.dataset.galleryStep));
      const dot = target.closest('button[data-gallery-index]');
      if (dot) setGallery(Number(dot.dataset.galleryIndex));
      const trigger = target.closest('[data-gallery-open]');
      if (trigger && dialog) {
        if (suppressClick) { suppressClick = false; return; }
        setGallery(Number(trigger.dataset.galleryOpen)); setLightboxImage(); openDialog(dialog, trigger);
      }
      const lightboxStep = target.closest('[data-lightbox-step]');
      if (lightboxStep) setGallery(galleryIndex + Number(lightboxStep.dataset.lightboxStep));
      const zoomControl = target.closest('[data-lightbox-zoom]');
      if (zoomControl) { zoom = Math.max(1, Math.min(4, zoom + Number(zoomControl.dataset.lightboxZoom))); panX = 0; panY = 0; applyZoom(); }
      if (target.closest('[data-lightbox-reset]')) resetZoom();
    });
    gallery?.addEventListener('pointerdown', event => { swipe = {x:event.clientX,y:event.clientY}; });
    gallery?.addEventListener('pointerup', event => {
      if (!swipe) return;
      const dx = event.clientX - swipe.x, dy = event.clientY - swipe.y;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) { suppressClick = true; setGallery(galleryIndex + (dx < 0 ? 1 : -1)); }
      swipe = null;
    });
    gallery?.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); setGallery(galleryIndex + (event.key === 'ArrowRight' ? 1 : -1)); }
    });
    stage?.addEventListener('pointerdown', event => {
      if (event.target instanceof Element && event.target.closest('button,output')) return;
      drag = {x:event.clientX,y:event.clientY,panX,panY};
      stage.setPointerCapture(event.pointerId);
    });
    stage?.addEventListener('pointermove', event => {
      if (!drag || zoom <= 1) return;
      panX = drag.panX + event.clientX - drag.x; panY = drag.panY + event.clientY - drag.y;
      stage.classList.add('is-dragging'); applyZoom();
    });
    stage?.addEventListener('pointerup', event => {
      if (!drag) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (zoom === 1 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) setGallery(galleryIndex + (dx < 0 ? 1 : -1));
      else if (Math.abs(dx) < 5 && Math.abs(dy) < 5) { zoom = zoom >= 4 ? 1 : zoom + 1; panX = 0; panY = 0; applyZoom(); }
      drag = null; stage.classList.remove('is-dragging');
    });
    stage?.addEventListener('pointercancel', () => { drag = null; stage.classList.remove('is-dragging'); });
    dialog?.addEventListener('keydown', event => {
      if (event.key === '+' || event.key === '=') { event.preventDefault(); zoom = Math.min(4, zoom + 1); applyZoom(); }
      if (event.key === '-') { event.preventDefault(); zoom = Math.max(1, zoom - 1); applyZoom(); }
      if (event.key === '0') resetZoom();
      if (event.key.startsWith('Arrow')) {
        event.preventDefault();
        if (zoom > 1 && !event.shiftKey) { panX += event.key === 'ArrowLeft' ? 72 : event.key === 'ArrowRight' ? -72 : 0; panY += event.key === 'ArrowUp' ? 72 : event.key === 'ArrowDown' ? -72 : 0; applyZoom(); }
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') setGallery(galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    form?.addEventListener('submit', async event => {
      event.preventDefault();
      const button = form.querySelector('[data-add-button]');
      const status = productRoot.querySelector('[data-product-message]');
      if (addBusy || button?.disabled) return;
      addBusy = true;
      if (button) button.disabled = true;
      bagMessage(status, message('adding', 'Adding to your Shopify bag…'));
      let addConfirmed = false;
      try {
        await nativeJsonRequest('cart/add.js', {method:'POST',body:new FormData(form),headers:{Accept:'application/json'}});
        addConfirmed = true;
        await readNativeCart();
        bagMessage(status, message('added'));
      } catch (error) {
        if (addConfirmed) {
          bagMessage(status, message('addedRefreshUnavailable', 'Added to your Shopify bag. We could not refresh the bag display. Check your bag before adding this item again.'), true);
        } else if (error.nativeRequestTimedOut) {
          bagMessage(status, message('addOutcomeUnknown', 'Adding to your bag timed out. The result is unknown; the item might have been added. Check your Shopify bag before adding it again.'), true);
        } else if (error.nativeResponseError) {
          bagMessage(status, error.message || message('error'), true);
        } else {
          bagMessage(status, message('addUnconfirmed', 'We could not confirm whether this item was added. The result is unknown. Check your Shopify bag before adding it again.'), true);
        }
      }
      finally {
        addBusy = false;
        if (button) button.disabled = !product.variants.find(value => String(value.id) === select?.value)?.available;
      }
    });
    setGallery(Number(gallery?.dataset.galleryIndex || 0));
  });
  async function initialise() {
    // Clear only legacy bag and migration-notice keys; preserve unrelated storage.
    try { localStorage.removeItem('flux-edition-cart'); localStorage.removeItem('flux-edition-cart-migration-error'); } catch { /* Do not clear unrelated storage. */ }
    if (config.wishlistMapUrl) {
      try { const response = await fetch(config.wishlistMapUrl, {credentials:'same-origin'}); if (response.ok) wishlistMap = await response.json(); } catch { /* Saved handles remain usable. */ }
    }
    syncWishlist();
    await renderWishlist();
  }
  initialise();
})();
